/**
 * The one place that talks to the backend.
 *
 * The API is guarded by a shared token. It is held in localStorage rather than
 * a cookie because there is no session to manage and no CSRF surface worth
 * inventing one for: an internal tool, a handful of people, a header.
 *
 * A 401 is not an error to surface as "something went wrong" — it means the
 * token is missing or wrong, which is a thing the operator can fix, so it gets
 * its own type and its own prompt.
 */

const TOKEN_KEY = 'reachout.token'

export class Unauthorized extends Error {
  constructor() {
    super('token missing or invalid')
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
  ) {
    super(detail)
  }
}

/** An error as a person can act on it.
 *
 *  The status code is half the diagnosis and `err.message` throws it away: a
 *  400 is a refusal that explains itself, a 502 is the request outliving the
 *  proxy, and a 500 is a traceback in the service log. Rendering all three as
 *  one sentence of prose made "Ask the model is broken" unanswerable without
 *  going to the server.
 */
export function describe(err: unknown): string {
  if (err instanceof ApiError) return `${err.status} — ${err.detail}`
  return String((err as Error)?.message ?? err)
}

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ''
}

export function setToken(value: string): void {
  if (value) localStorage.setItem(TOKEN_KEY, value)
  else localStorage.removeItem(TOKEN_KEY)
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'X-Reachout-Token': token } : {}),
      ...(init.headers ?? {}),
    },
  })

  if (res.status === 401) throw new Unauthorized()

  if (!res.ok) {
    // FastAPI puts the reason in `detail`, and those reasons are written to be
    // read by a person — a refusal explains itself, so show it verbatim rather
    // than replacing it with a generic message.
    let detail = `${res.status} ${res.statusText}`
    try {
      const body = await res.json()
      if (typeof body.detail === 'string') detail = body.detail
      else if (Array.isArray(body.detail)) detail = body.detail.map((d: { msg?: string }) => d.msg ?? JSON.stringify(d)).join('; ')
      else if (body.detail && typeof body.detail === 'object' && Array.isArray(body.detail.refused))
        detail = body.detail.refused.join('; ')
      else if (body.detail && typeof body.detail === 'object') detail = JSON.stringify(body.detail)
    } catch {
      /* non-JSON body; keep the status line */
    }
    throw new ApiError(res.status, detail)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

/** For endpoints that return markup rather than JSON — the style previews.
 *  Shares the auth and error handling; only the body decoding differs. */
async function requestText(path: string): Promise<string> {
  const token = getToken()
  const res = await fetch(`/api${path}`, {
    headers: token ? { 'X-Reachout-Token': token } : {},
  })
  if (res.status === 401) throw new Unauthorized()
  if (!res.ok) throw new ApiError(res.status, `${res.status} ${res.statusText}`)
  return res.text()
}

const get = <T,>(path: string) => request<T>(path)
const post = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body) })
const patch = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
const put = <T,>(path: string, body: unknown) =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) })
const del = <T,>(path: string) => request<T>(path, { method: 'DELETE' })

// ---------------------------------------------------------------------------
// Types — mirrors of what the API returns, no more
// ---------------------------------------------------------------------------

export interface Filter {
  id: number
  name: string
  title: string
  description: string | null
  when: string | null
  /** No condition at all: matches everyone in the mailable base. Countable,
   *  and refused as the audience of an enabled campaign. */
  unconditional: boolean
  /** The activity window this condition's windowed counts are measured over,
   *  in days. Null unless the condition names one — and required when it
   *  does, since `visits_in_window > 100` means nothing without a width. */
  window_days: number | null
  parked: boolean
  /** Campaigns pointed at this audience, and how many of them are enabled. */
  campaigns?: number
  enabled_campaigns?: number
  notes: string | null
  created_at: string
  updated_at: string
  /** How many of the six slots have an APPROVED fragment naming this segment.
   *  Zero means a run composes six generic sentences: composition never fails,
   *  so nothing else reports it. */
  slots_with_own_copy?: number
  /** Fragments written for this segment and not yet approved. */
  awaiting_approval?: number
  legacy_group?: string | null
}

/**
 * One name a condition may be written with.
 *
 * The whole vocabulary is 37 entries and never changes at runtime, which is
 * what makes a picker possible at all. It is the ENGINE's key set, not the
 * config catalogue: `language` is a fully documented band that derive() never
 * produces, and offering it would let someone build a condition that saves
 * cleanly and then kills a run mid-scan.
 */
export interface VocabularyEntry {
  name: string
  kind: 'band' | 'number' | 'date' | 'flag'
  source: string | null
  note: string | null
  /** Bands only, in ordinal order — dead, low, medium, high. */
  values?: { value: string; rule: string }[]
  unit?: string | null
  /** Flags only. */
  status?: string | null
  origin?: string | null
  /** Numbers only — what the bound counts. `days` gets the period presets;
   *  `count` gets a plain number, and its period is the filter's window. */
  measure?: 'days' | 'count'
  /** Numbers only — which way time runs, so "within 7" reads as "the last
   *  week" for a days-since signal and "the next week" for days-to-renewal. */
  direction?: 'past' | 'future'
  /** Numbers only. A windowed count is computed only when a condition names
   *  it, and then only at the width the filter declares. */
  windowed?: boolean
  /** Numbers only, and the field most likely to be misread: no bound matches
   *  a NULL in either direction, because the event did not happen. */
  null_means?: string | null
}

/** One requirement a condition places on an account, with what it means. */
export interface Clause {
  raw: string
  name: string | null
  /** 'is' | 'is not' | 'is one of' | 'is true' | 'is false'
   *  | 'is at most' | 'is at least' | 'is under' | 'is over' */
  test: string | null
  values: string[]
  kind: 'band' | 'number' | 'date' | 'flag' | 'unknown'
  measure?: 'days' | 'count'
  direction?: 'past' | 'future'
  windowed?: boolean
  null_means?: string | null
  source?: string | null
  unit?: string | null
  note?: string | null
  status?: string | null
  origin?: string | null
  /** Only the bands the clause actually names, with their cut-offs. */
  rules?: { band: string; rule: string }[]
  all_bands?: string[]
}

/**
 * Everything a run applies, in the order it applies it.
 *
 * `gates` and `preceding` are the two parts a filter's own condition does not
 * mention and cannot override — the suppression baked into the signal SQL, and
 * the filters that get first refusal on an account.
 */
export interface Coverage {
  group: string | null
  slots: string[]
  own: Record<string, { approved: number; draft: number; pending: number }>
  generic: Record<string, number>
  slots_with_own_copy: number
  slots_total: number
  awaiting_approval: number
  verdict: string
}

export interface FilterBrief {
  filter: Filter
  coverage: Coverage
  gates: { key: string; rule: string }[]
  condition: {
    decomposable: boolean
    catch_all: boolean
    clauses: Clause[]
    why?: string
  }
  total_filters: number
}

export interface Run {
  id: number
  filter: string
  run_date: string
  label: string | null
  row_count: number
  /** The row cap the scan ran under. Null = uncapped; a number means the list
   *  is a sample and a campaign built on it would mail only that many. */
  scan_limit: number | null
  triggered_by: string
  created_at: string
  member_count: number
}

export interface Member {
  /** Account.AccountID — the login. */
  account_id: number
  /** Company.CompanyID — the customer (D116). */
  company_id: number | null
  snapshot: Record<string, unknown>
  source: string
  created_at: string
}

export interface MembersPage {
  run_id: number
  /** After filtering. Paging is computed from this, or the operator cannot
   *  reach the fifth match of four shown. */
  total: number
  /** Before it, so the screen can say what was narrowed away. */
  total_unfiltered: number
  limit: number
  offset: number
  masked: boolean
  members: Member[]
}

/** What the Accounts tab narrows by. Every one of these is also a column on
 *  screen: filtering by something invisible gives a result nobody can check by
 *  looking at it. */
export interface MemberQuery {
  limit?: number
  offset?: number
  masked?: boolean
  /** Ask how many, not which. The account index only — `accounts` comes back
   *  empty and no snapshot is read. */
  count_only?: boolean
  /** Account id, name, company or domain. Matches whether or not the view is
   *  masked — masking hides identity on screen, it is not an access control. */
  q?: string
  // Snapshot columns; answered in SQL.
  stage?: string
  paying?: boolean
  verified?: boolean
  activated?: boolean
  customized?: boolean
  in_eu?: boolean
  source?: string
  visits_min?: number
  visits_max?: number
  consents_min?: number
  consents_max?: number
  idle_min?: number
  idle_max?: number
  /** DERIVED bands — the vocabulary the segment conditions are written in, and
   *  not columns. They cost a pass over the run (about half a second on the
   *  largest segment), and only when one is actually set. */
  commercial_state?: string
  funnel_stage?: string
  traffic_band?: string
  engagement_recency?: string
  quota_pressure?: string
}

/** One derived value, with where it came from and the cut-off it fell on. */
export interface Signal {
  name: string
  value: unknown
  kind: 'band' | 'number' | 'date' | 'flag' | 'unknown'
  source: string | null
  unit: string | null
  note: string | null
  /** For a band: the rule THIS account satisfied, not the whole ladder. */
  rule: string | null
  all_bands: string[] | null
}

/** What was SENT. The permanent record, and it holds no body on purpose. */
export interface SentEmail {
  sent_at: string
  filter: string | null
  outcome: string
  subject: string | null
  reason: string | null
}

/** What was COMPOSED. Exists for people we never wrote to, which is most of
 *  them. `body` comes back only when the view is unmasked: prose carries the
 *  reader's name and domain and cannot be masked without being mangled. */
export interface ComposedEmail {
  draft_id: number
  campaign: string
  filter: string
  status: string
  created_at: string
  reviewed_at: string | null
  subject: string
  refined: boolean
  model: string | null
  body: string | null
}

export interface MemberDetail {
  run_id: number
  account_id: number
  source: string
  created_at: string
  masked: boolean
  snapshot: Record<string, unknown>
  signals: Signal[]
  history: SentEmail[]
  drafts: ComposedEmail[]
  selected_by: { name: string; title: string | null; when: string | null }
}

/** One row of the account index. `last_seen_in` is the run the snapshot beside
 *  it came from — the only thing that can say how old these numbers are. */
export interface AccountRow {
  account_id: number
  company_id: number | null
  snapshot: Record<string, unknown>
  source: string
  created_at: string
  last_seen_in: { run_id: number; filter: string; run_date: string }
}

export interface AccountsPage {
  /** True when this was a count, not a page: `accounts` is empty because no
   *  snapshot was read, not because nothing matched. */
  counted_only: boolean
  /** After filtering. Paging is computed from this. */
  total: number
  /** Every account we hold a snapshot for, so the screen can say what it
   *  narrowed from — and that this index is not the whole CRM. */
  total_accounts: number
  limit: number
  offset: number
  masked: boolean
  accounts: AccountRow[]
}

/** A segment that has put this account in a list at some point. */
export interface SelectedBy {
  run_id: number
  name: string
  title: string | null
  when: string | null
  run_date: string
  source: string
  created_at: string
}

/** The run-free detail. Identical to MemberDetail but for `selected_by`, which
 *  is a list: an account can sit in six saved lists cut by four conditions, and
 *  naming one of them away from a run would be an arbitrary choice presented as
 *  a fact. */
export interface AccountDetailBody extends Omit<MemberDetail, 'selected_by'> {
  selected_by: SelectedBy[]
}

export interface AdviceSuggestion {
  title: string
  why: string
  campaign: string
  subject: string
  body: string
  cta: string
  signals: string[]
  caution: string
}

/** The model's answer to "what could we send this person".
 *  Advice: `stored` is always false, nothing is created. */
export interface Advice {
  account_id: number
  model: string
  asked_as: { filter: string; title: string | null }
  history_considered: number
  stored: boolean
  ok: boolean
  verdict?: 'send' | 'skip'
  reason?: string
  subject?: string
  body?: string
  cta?: string
  signals?: string[]
  suggestions?: AdviceSuggestion[]
  rejected_suggestions?: { title: string; subject?: string; problems: string[] }[]
  problems?: string[]
  rejected_output?: { subject?: string; body?: unknown }
}

export interface RunPreview {
  /** The filters this scan was asked about. One, from the People tab. */
  filters: string[]
  filter: string | null
  /** Matches for `filter` when exactly one was asked. */
  matched: number | null
  matched_each: Record<string, number>
  scanned: number
  /** Every filter's population in this scan. Non-exclusive: the counts sum
   *  to more than `scanned`, because an account can match several. */
  matched_by: Record<string, number>
  /** For each requested filter, how many of its matches also match each other
   *  filter — the readers two campaigns would share. */
  overlap: Record<string, Record<string, number>>
  /** Filters whose condition needs a different activity window and so could
   *  not be counted in this scan. */
  not_counted: string[]
  unconditional: string[]
  window_days: number | null
}

export interface Job {
  id: number
  kind: string
  status: 'running' | 'completed' | 'failed' | 'cancelled'
  started_at: string
  ended_at: string | null
  triggered_by: string
  detail: Record<string, unknown>
}

export interface ModelInfo {
  slug: string
  name: string
  context_length: number | null
  prompt_per_million: number | null
  completion_per_million: number | null
  free: boolean
}

export interface SettingsPayload {
  settings: { key: string; value: string; writable: boolean }[]
  secrets_configured: Record<string, boolean>
  /** Deployment configuration, read from the environment and not editable.
   *  The sender's value is exposed (it is an address, not a credential)
   *  because which mailbox it is decides whether reply-seeking copy is
   *  approvable. */
  deployment?: {
    sender_identity: string
    unsubscribe_base: string
    note: string
  }
}

export interface DebugRecipient {
  address: string
  why: string
}

export interface DryRunResult {
  cycle_id: number
  approved: number
  cleared: number
  refused: number
  refusals: { account_id: number; company_id?: number | null; reason: string }[]
  note: string
  mailster_consulted: boolean
  over_cap?: boolean
  confirm_token?: string
  /** D116, service cycles only: how the approved people came in by company,
   *  and how many the send-time login re-check refuses (already in `refusals`). */
  identity?: { service: true; approved_by_company: number; refused_by_identity_check: number }
}

/** One unsent draft and who it would go to NOW (D116). `to` is resolved by the
 *  same recipient check dispatch runs; null with a `reason` when a gate refuses
 *  it. Looked up live for this screen and never stored. */
export interface DraftRecipient {
  draft_id: number
  status: string
  /** Account.AccountID — the login the mail goes to. */
  account_id: number
  /** Company.CompanyID — the customer that login belongs to. */
  company_id: number | null
  domain: string | null
  to: string | null
  ok: boolean
  reason: string | null
}

export interface SesState {
  checked: boolean
  region: string | null
  config_set: string | null
  /** Already folded into the ladder's blockers; repeated for display. */
  problems: string[]
  evidence: Record<string, unknown>
}

export interface StyleInfo {
  name: string
  title: string
  tone: string | null
  best_for: string | null
  /** What this style is WRONG for. The only field here that prevents a
   *  mistake, which is why the list leads with it. */
  against: string | null
  images: string | null
  dark_mode: unknown
  width: unknown
  placeholders: string[]
  carries_footer: boolean
  /** Non-empty means the style must not be used — chiefly a missing
   *  {{footer_notice}}, i.e. no unsubscribe link. */
  problems: string[]
}

/** One predefined copy fragment, as the Library screen reads it.
 *
 *  Everything past `note` is derived rather than stored — position, which
 *  segments a condition names, which merge fields the text pulls in. The file
 *  stays the only place a fragment is written. */
export interface Fragment {
  id: string
  slot: string
  /** Index within the slot. Order IS specificity: first match wins, so this
   *  decides whether the fragment is ever selected. */
  position: number
  status: 'approved' | 'pending' | 'draft' | string
  when: string | null
  tone: string | null
  variant: string | null
  text: string
  note: string | null
  source: 'inherited' | 'override' | string
  /** Group ids named in the condition. Empty means it is cross-cutting and can
   *  take an account out of any segment. */
  segments: string[]
  signals: string[]
  unconditional: boolean
  /** Only `approved` composes. The rest are visible here and cannot reach a
   *  customer — which is the whole reason this screen exists. */
  composes: boolean
  /** Merge fields the text names. A fragment naming one an account has empty
   *  is SKIPPED, so this is also the list of ways it can fall through. */
  uses: string[]
  declared: string[]
  undeclared: string[]
  unused_declaration: string[]
  unknown_fields: string[]
  meanings: Record<string, string | null>
  /** Sits below the unconditional fallback in its slot, so it can never win. */
  unreachable: boolean
}

export interface DataField {
  name: string
  meaning: string | null
  used_by: string[]
  uses: number
  composing_uses: number
  slots: string[]
  in_dictionary: boolean
  unknown?: boolean
}

export interface ConditionSignal {
  name: string
  meaning: string | null
  kind: string | null
  values: string[]
  used_by: string[]
  uses: number
  known_to_engine: boolean
}

export interface LibraryPayload {
  fragments: Fragment[]
  fields: DataField[]
  signals: ConditionSignal[]
  summary: {
    total: number
    by_status: Record<string, number>
    by_slot: Record<string, { total: number; approved: number; waiting: number }>
    with_data: number
    unreachable: string[]
    undeclared: string[]
    unknown_fields: string[]
    slots: string[]
  }
  note: string
}

export interface SegmentCoverage {
  filter: string
  title: string
  parked: boolean
  group: string
  slots: string[]
  own: Record<string, { approved: number; draft: number; pending: number }>
  generic: Record<string, number>
  slots_with_own_copy: number
  slots_total: number
  awaiting_approval: number
  verdict: string
}

export type CopySlot = 'subject' | 'opening' | 'evidence' | 'pitch' | 'reassurance' | 'cta'
export const COPY_SLOTS: CopySlot[] = ['subject', 'opening', 'evidence', 'pitch', 'reassurance', 'cta']

/** The standing program (D83). One row holds everything a tick needs. */
export interface Campaign {
  id: number
  name: string
  slug: string
  description: string | null
  filter_id: number
  filter: string
  filter_title: string
  filter_parked: boolean
  filter_when: string | null
  legacy_group: string | null
  enabled: boolean
  /** Present on GET /campaigns/{id}; the list omits it. */
  system_prompt?: string
  system_prompt_chars: number
  copy: Partial<Record<CopySlot, string>> & { cta_action?: string }
  style: string | null
  message_class: 'campaign' | 'service'
  cooldown_days: number | null
  max_sends: number | null
  interval_hours: number
  timezone: string
  next_run_at: string | null
  last_run_at: string | null
  review_mode: 'human' | 'auto'
  dispatch_mode: 'manual' | 'auto'
  /** A person read the copy and the prompt (D88). Cleared by any edit. */
  copy_approved: boolean
  copy_approved_at: string | null
  copy_approved_by: string | null
  /** A person typed the slug to let the scheduler send (D89). Cleared by any edit. */
  armed: boolean
  auto_dispatch_armed_at: string | null
  auto_dispatch_armed_by: string | null
  /** A person opened this campaign to agent release (D108). Cleared by any edit. */
  agent_release_allowed: boolean
  agent_release_allowed_at: string | null
  agent_release_allowed_by: string | null
  /** A person waived the 14-day gap and 30-day cap for this service campaign (D112). Cleared by any edit. */
  cadence_waived: boolean
  cadence_waived_at: string | null
  cadence_waived_by: string | null
  notes: string | null
  created_at: string
  updated_at: string
  // List view extras
  last_cycle_id?: number | null
  last_cycle_status?: string | null
  awaiting_review?: number
  approved?: number
  sent_total?: number
  /** Drafts a person can still act on: those in cycles not sent, cancelled or failed. */
  open_awaiting_review?: number
  open_approved?: number
  /** Open cycles of this campaign that have stopped moving (D106). */
  stale_cycles?: number
  /** Accounts those cycles are holding out of every later tick. */
  stale_holding?: number
  // Detail view extras
  cycles?: Cycle[]
  exclusions?: Exclusion[]
  enable_blockers?: string[]
  arm_blockers?: string[]
}

export interface Exclusion {
  account_id: number
  reason: string | null
  added_by: string
  created_at: string
}

export type CycleStatus =
  | 'scanning' | 'composing' | 'refining' | 'awaiting_review' | 'approved'
  | 'dispatching' | 'sent' | 'failed' | 'cancelled'

/** One tick of a campaign (D86). */
export interface Cycle {
  id: number
  campaign_id: number
  campaign: string
  campaign_name: string
  run_id: number | null
  status: CycleStatus
  triggered_by: string
  started_at: string
  ended_at: string | null
  detail: Record<string, unknown>
  members: number
  skipped: number
  drafts: number
  awaiting_review: number
  approved: number
  rejected: number
  sent: number
  /** Present on GET /cycles/{id}. */
  skips?: CycleSkip[]
  // Staleness, computed on every read of a cycle (D106).
  /** Its accounts are in flight: no later tick of this campaign drafts them. */
  open: boolean
  /** How many accounts that is. Zero once the cycle closes. */
  holding: number
  age_hours: number
  stale: boolean
  /** `abandoned`: no worker left to finish it. `waiting`: nobody got to it. */
  stale_kind: 'abandoned' | 'waiting' | null
  stale_reason: string | null
  what_to_do: string | null
}

/** GET /cycles/stale — the stuck queue, worst first. */
export interface StaleReport {
  stale: number
  holding: number
  abandoned: number
  waiting: number
  thresholds: { stale_after_days: number; abandoned_after_hours: number; tuned_in: string }
  cycles: Cycle[]
  note: string
}

export interface CycleSkip {
  account_id: number
  reason: 'cooldown' | 'in_flight' | 'max_sends' | 'account_cap' | 'suppressed' | 'excluded' | 'parked' | 'delivery'
  detail: string | null
}

export interface SchedulerStatus {
  last_tick: Job | null
  recent: Job[]
  stale: boolean
  overdue_campaigns: number
  armed_campaigns: number
  kill_switch_on_this_process: boolean
  note: string
}

/** What SES told us happened after a send, and how much of it we hear. */
export interface DeliveryReport {
  configured: boolean
  topics: string[]
  events: number
  by_type: { event_type: string; count: number; latest: string | null }[]
  suppressing: { hard_bounced: number; complained: number; soft_bounced: number }
  rows: { account_id: number; hard_bounced: boolean; complained: boolean }[]
  note: string
}

/** Opt-outs we accepted, and the ones the other systems still do not know about. */
export interface UnsubscribeReport {
  total: number
  unreconciled: number
  configured: boolean
  rows: {
    account_id: number
    source: string
    campaign_id: number | null
    created_at: string
    reconciled_at: string | null
  }[]
  note: string
}

export interface DraftRow {
  id: number
  cycle_id: number
  campaign_id: number
  campaign: string
  /** Account.AccountID — the login. */
  account_id: number
  /** Company.CompanyID — the customer (D116). Null for drafts from before it. */
  company_id: number | null
  status: string
  original_subject: string
  refined_subject: string | null
  llm_model: string | null
  has_refined: boolean
}

export interface Draft extends DraftRow {
  original_body: string
  refined_body: string | null
  /** The opening line, stored apart from the body so refining cannot rewrite
   *  it into a bare vocative. */
  greeting: string | null
  prompt_used: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  refine_meta?: {
    angle?: string | null
    changes?: string[]
    signals?: string[]
    usage?: Record<string, number | null>
  }
  refined_at?: string | null
  compose_meta?: Record<string, unknown>
}


export interface ExperimentsPayload {
  holdout: { enabled: boolean; share: number | null; note: string }
  experiments: {
    id: string
    title: string
    enabled: boolean
    arms: string[]
    slots: string[]
    when: string | null
    hypothesis: string | null
    metric: string | null
  }[]
}

export interface ArmPerformance {
  rows: {
    experiment: string
    arm: string
    sent: number
    delivered: number
    opened: number
    clicked: number
    bounced: number
    complained: number
  }[]
  sent_total: number
  measurable: boolean
  why_empty: string | null
}

export interface Health {
  ok: boolean
  databases: Record<string, string>
  send_enabled: boolean
}

/** What a health check found, including the two ways it can find nothing.
 *
 *  `unreachable` is its own state and not an absence. The header used to hold
 *  `Health | null` and set null on any error, which erased the difference
 *  between "not asked yet", "the server is gone" and "a database is down" —
 *  and since the strip rendered nothing for null, the indicator DISAPPEARED
 *  exactly when it had something to say. */
export type HealthReport =
  | { state: 'ok' | 'degraded'; health: Health; at: number }
  | { state: 'unreachable'; error: string; at: number }

export const api = {
  health: () => get<Health>('/health'),

  /** Health, as a value rather than an exception.
   *
   *  `/api/health` answers 503 when a database is down, and `request()` throws
   *  on any non-2xx — so the ordinary helper turns the most informative
   *  response the endpoint gives into a thrown error whose body has already
   *  been read for a `detail` field that health responses do not have. This
   *  reads the body either way and never rejects, because a monitor that can
   *  throw is a monitor with a state it cannot display. */
  healthReport: async (): Promise<HealthReport> => {
    const at = Date.now()
    const token = getToken()
    try {
      const res = await fetch('/api/health', {
        headers: token ? { 'X-Reachout-Token': token } : {},
      })
      const health = (await res.json()) as Health
      return { state: health.ok ? 'ok' : 'degraded', health, at }
    } catch (err) {
      // A network-level failure: the server is not answering at all. This is
      // what a stopped backend looks like, and it is the case the old code
      // rendered as nothing.
      return { state: 'unreachable', error: String((err as Error).message), at }
    }
  },

  filters: () => get<Filter[]>('/filters'),
  /** The names and values a condition can be written with. Static per build —
   *  fetch once and keep it; it describes the engine, not any account. */
  vocabulary: () => get<VocabularyEntry[]>('/filters/vocabulary'),
  filter: (name: string) => get<Filter>(`/filters/${encodeURIComponent(name)}`),
  filterBrief: (name: string) =>
    get<FilterBrief>(`/filters/${encodeURIComponent(name)}/brief`),
  updateFilter: (name: string, body: Partial<Filter> & { when?: string | null }) =>
    patch<{ updated: string }>(`/filters/${encodeURIComponent(name)}`, body),
  createFilter: (body: Record<string, unknown>) =>
    post<{ id: number; name: string; parked: boolean }>('/filters', body),
  deleteFilter: (name: string) => del<{ deleted: string }>(`/filters/${encodeURIComponent(name)}`),

  runs: (filter?: string) => get<Run[]>(`/runs${filter ? `?filter=${encodeURIComponent(filter)}` : ''}`),
  startRun: (body: { filter: string; label?: string; limit?: number }) =>
    post<{ job_id: number; status: string }>('/runs', body),
  previewRun: (body: { filter: string; limit?: number; cancel_token?: string }) =>
    post<RunPreview>('/runs/preview', body),
  /** Stop a scan already running.
   *
   *  `job:{id}` for a background run; for a preview, the token the caller
   *  generated and sent WITH the request — a synchronous endpoint cannot hand
   *  back an id to cancel with, because by the time it responds there is
   *  nothing left to cancel. */
  cancelScan: (key: string) =>
    post<{ key: string; stopped: boolean; note: string }>('/runs/cancel', { key }),
  /** A token to make one scan cancellable. Random, not sequential: two tabs
   *  must not be able to cancel each other's scans by coincidence. */
  cancelToken: () =>
    `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`,
  job: (id: number) => get<Job>(`/runs/jobs/${id}`),
  members: (runId: number, opts: MemberQuery = {}) => {
    const q = new URLSearchParams()
    // Empty is "no filter", not "match the empty string" — a select reset to
    // its blank option must widen the result, never narrow it to nothing.
    for (const [key, value] of Object.entries(opts)) {
      if (value === undefined || value === null || value === '') continue
      q.set(key, String(value))
    }
    return get<MembersPage>(`/runs/${runId}/members?${q}`)
  },
  memberDetail: (runId: number, accountId: number, masked: boolean) =>
    get<MemberDetail>(`/runs/${runId}/members/${accountId}?masked=${masked}`),
  advise: (runId: number, accountId: number) =>
    post<Advice>(`/runs/${runId}/members/${accountId}/advise`, {}),

  /** The account INDEX — every snapshot we hold, deduped to the newest per
   *  account. Same filter names as `members`, because they are built from the
   *  same predicates on the server (lib/members.py). */
  accounts: (opts: MemberQuery = {}) => {
    const q = new URLSearchParams()
    for (const [key, value] of Object.entries(opts)) {
      if (value === undefined || value === null || value === '') continue
      q.set(key, String(value))
    }
    return get<AccountsPage>(`/accounts?${q}`)
  },
  /** One account with no run in the picture. `selected_by` is a list here:
   *  away from a particular run there is no single rule that selected them. */
  account: (accountId: number, masked: boolean) =>
    get<AccountDetailBody>(`/accounts/${accountId}?masked=${masked}`),
  adviseAccount: (accountId: number, filter?: string) =>
    post<Advice>(`/accounts/${accountId}/advise`, filter ? { filter } : {}),
  /** Marketing lists only, named by both ids, which must agree (D116). A
   *  service member comes in by company through the MCP add_cycle_members. */
  addMember: (runId: number, accountId: number, companyId: number) =>
    post<{ added: boolean; account_id: number; company_id: number; domain: string | null }>(
      `/runs/${runId}/members`, { account_id: accountId, company_id: companyId }),
  removeMember: (runId: number, accountId: number) =>
    del<{ removed: boolean }>(`/runs/${runId}/members/${accountId}`),

  styles: () =>
    get<{ default: string; styles: StyleInfo[]; note: string }>('/styles'),
  /** The rendered HTML, fetched rather than framed by URL.
   *
   *  An <iframe src> cannot carry the X-Reachout-Token header, so a framed
   *  URL 401s wherever a token is configured — i.e. everywhere but local
   *  dev, which is the worst possible place for it to work. Passing the
   *  token in the query string instead would put a shared secret in a URL,
   *  in history and in any log along the way. So: fetch it, then hand the
   *  markup to the iframe as srcdoc. */
  stylePreview: (name: string, draftId?: number) =>
    requestText(
      `/styles/${encodeURIComponent(name)}/preview` +
        (draftId != null ? `?draft_id=${draftId}` : ''),
    ),
  styleText: (name: string) =>
    get<{ text: string }>(`/styles/${encodeURIComponent(name)}/text`),
  chooseStyle: (campaignId: number, style: string) =>
    put<{ campaign_id: number; style: string; copy_approval_withdrawn?: boolean }>(
      `/campaigns/${campaignId}/style`, { style }),

  library: () => get<LibraryPayload>('/library'),
  libraryCoverage: () => get<{ segments: SegmentCoverage[] }>('/library/coverage'),

  settings: () => get<SettingsPayload>('/settings'),
  models: (opts: { search?: string; free_only?: boolean } = {}) => {
    const q = new URLSearchParams()
    if (opts.search) q.set('search', opts.search)
    if (opts.free_only) q.set('free_only', 'true')
    return get<{ count: number; models: ModelInfo[] }>(`/settings/models?${q}`)
  },

  // ---- campaigns: the standing programs --------------------------------
  campaigns: () => get<Campaign[]>('/campaigns'),
  campaign: (id: number) => get<Campaign>(`/campaigns/${id}`),
  createCampaign: (body: {
    name: string
    filter: string
    slug?: string
    description?: string
    system_prompt?: string
    copy?: Record<string, string>
    style?: string
    message_class?: string
    cooldown_days?: number | null
    max_sends?: number | null
    interval_hours?: number
    timezone?: string
    review_mode?: 'human' | 'auto'
    dispatch_mode?: 'manual' | 'auto'
    notes?: string
  }) => post<Campaign>('/campaigns', body),
  updateCampaign: (id: number, body: Record<string, unknown>) =>
    patch<{ updated: number; fields: string[]; copy_approval_withdrawn?: boolean; disarmed?: boolean }>(
      `/campaigns/${id}`, body),
  deleteCampaign: (id: number) => del<{ deleted: number }>(`/campaigns/${id}`),
  /** A person read the copy and the prompt (D88). */
  approveCopy: (id: number, approved_by: string) =>
    post<{ campaign_id: number; copy_approved_by: string }>(`/campaigns/${id}/approve-copy`, { approved_by }),
  enableCampaign: (id: number) =>
    post<{ campaign_id: number; enabled: boolean; next_run_at: string | null }>(`/campaigns/${id}/enable`, {}),
  disableCampaign: (id: number) =>
    post<{ campaign_id: number; enabled: boolean }>(`/campaigns/${id}/disable`, {}),
  /** The slug is TYPED by the person arming (D89). Nothing pre-fills it. */
  armCampaign: (id: number, typed_slug: string, armed_by: string) =>
    post<{ campaign_id: number; armed: boolean; armed_by: string }>(`/campaigns/${id}/arm`, { typed_slug, armed_by }),
  disarmCampaign: (id: number, by: string) =>
    post<{ campaign_id: number; armed: boolean }>(`/campaigns/${id}/disarm`, { by }),
  /** Open or close a campaign to agent release (D108). Opening needs a person's name. */
  setAgentRelease: (id: number, allowed: boolean, by: string) =>
    post<{ campaign_id: number; agent_release_allowed: boolean }>(
      `/campaigns/${id}/agent-release`, { allowed, by }),
  /** Waive or restore the account send gap for a service campaign (D112). Waiving needs a person's name. */
  setCadenceWaiver: (id: number, waived: boolean, by: string) =>
    post<{ campaign_id: number; cadence_waived: boolean }>(
      `/campaigns/${id}/cadence-waiver`, { waived, by }),
  addExclusion: (id: number, account_id: number, reason?: string) =>
    post<{ excluded: boolean }>(`/campaigns/${id}/exclusions`, { account_id, reason }),
  removeExclusion: (id: number, account_id: number) =>
    del<{ removed: boolean }>(`/campaigns/${id}/exclusions/${account_id}`),
  /** One tick, now, as a background job. Poll `job`. Never dispatches. */
  runCampaign: (id: number, body: { refine?: boolean; model?: string; limit?: number } = {}) =>
    post<{ job_id: number; status: string }>(`/campaigns/${id}/run`, body),

  // ---- cycles: one tick each ---------------------------------------------
  cycles: (campaignId?: number, limit = 50) =>
    get<Cycle[]>(`/cycles?limit=${limit}${campaignId != null ? `&campaign_id=${campaignId}` : ''}`),
  cycle: (id: number) => get<Cycle>(`/cycles/${id}`),
  /** Open cycles nothing is moving, and who they hold in flight (D106). */
  staleCycles: (campaignId?: number) =>
    get<StaleReport>(`/cycles/stale${campaignId != null ? `?campaign_id=${campaignId}` : ''}`),
  cancelCycle: (id: number) => post<{ cycle_id: number; status: string }>(`/cycles/${id}/cancel`, {}),
  compose: (cycleId: number, replace = false) =>
    post<{
      members: number
      composed: number
      skipped_existing: number
      with_problems: number
      problems: { account_id: number; problems: string[] }[]
      note: string
    }>(`/cycles/${cycleId}/compose`, { replace }),
  refine: (
    cycleId: number,
    body: {
      draft_id?: number
      model?: string
      system_prompt?: string
      save_prompt?: boolean
      limit?: number
      status_filter?: string
      only_unrefined?: boolean
      allow_generic?: boolean
    },
  ) => post<{ job_id: number; model: string }>(`/cycles/${cycleId}/refine`, body),
  drafts: (opts: { cycle_id?: number; campaign_id?: number; status_filter?: string; limit?: number } = {}) => {
    const q = new URLSearchParams()
    if (opts.cycle_id != null) q.set('cycle_id', String(opts.cycle_id))
    if (opts.campaign_id != null) q.set('campaign_id', String(opts.campaign_id))
    if (opts.status_filter) q.set('status_filter', opts.status_filter)
    if (opts.limit != null) q.set('limit', String(opts.limit))
    return get<{ total: number; drafts: DraftRow[] }>(`/drafts?${q}`)
  },
  draft: (id: number) => get<Draft>(`/drafts/${id}`),
  review: (draft_ids: number[], verdict: 'approved' | 'rejected' | 'pending', reviewed_by = 'operator') =>
    post<{ updated: number; read_alone?: number[] }>('/drafts/review', { draft_ids, verdict, reviewed_by }),
  setSetting: (key: string, value: string) => put<unknown>(`/settings/${key}`, { value }),
  schedulerStatus: () => get<SchedulerStatus>('/settings/scheduler'),
  delivery: () => get<DeliveryReport>('/delivery'),
  unsubscribes: () => get<UnsubscribeReport>('/unsubscribes'),
  experiments: () => get<ExperimentsPayload>('/experiments'),
  armPerformance: () => get<ArmPerformance>('/experiments/performance'),

  sendReadiness: (cycleId?: number) =>
    get<{
      can_send: boolean
      blockers: string[]
      gates: Record<string, string>
      ses?: SesState
      approvals?: { approved: number; approved_by_agent: number; approved_by_scheduler: number }
      policy?: {
        campaign?: string; enabled?: boolean; copy_approved?: boolean
        review_mode?: string; dispatch_mode?: string; armed?: boolean; cycle_status?: string
      }
      note: string
      real_send_route_registered?: boolean
      redirect_route_registered?: boolean
    }>(`/settings/send-readiness${cycleId != null ? `?cycle_id=${cycleId}` : ''}`),
  debugRecipients: () => get<{ recipients: DebugRecipient[] }>('/send/debug-recipients'),
  sendDryRun: (cycleId: number, opts: { skip_mailster?: boolean; for_redirect?: boolean } = {}) =>
    post<DryRunResult>(`/send/dry-run?cycle_id=${cycleId}`, {
      skip_mailster: opts.skip_mailster ?? false,
      for_redirect: opts.for_redirect ?? false,
    }),
  /** A test send takes the dry run's cleared count, not a typed token — the
   *  server re-checks it and refuses if drafts moved in between. */
  sendRedirect: (cycleId: number, body: { cleared: number; redirect_to: string }) =>
    post<{
      cycle_id: number
      committed: boolean
      counted: Record<string, number>
      aborted: string | null
      sample: { account_id: number; outcome: string; reason: string | null; domain: string | null }[]
    }>(`/send/redirect?cycle_id=${cycleId}`, body),
  /** Manual dispatch. The token is TYPED from the dry run: {cycle_id}:{cleared}. */
  sendReal: (cycleId: number, confirm_token: string, sent_by: string) =>
    post<{
      cycle_id: number
      committed: boolean
      counted: Record<string, number>
      aborted: string | null
    }>(`/send/real?cycle_id=${cycleId}`, { confirm_token, sent_by }),
  /** Each unsent draft's To address, company and domain, resolved live (D116). */
  sendRecipients: (cycleId: number) =>
    get<{ cycle_id: number; service: boolean; drafts: DraftRecipient[] }>(
      `/send/recipients?cycle_id=${cycleId}`),
  /** What the authorize panel shows (D108). Draft hashes are never returned. */
  sendAuthorization: (cycleId: number) =>
    get<SendAuthorizationState>(`/send/authorization?cycle_id=${cycleId}`),
  /** A person read the approved drafts and lets an agent release exactly them. */
  sendAuthorize: (cycleId: number, authorized_by: string, ttl_minutes: number) =>
    post<{ id: number; drafts: number; cleared: number; authorized_by: string; expires_at: string }>(
      `/send/authorize?cycle_id=${cycleId}`, { authorized_by, ttl_minutes }),
  revokeSendAuthorization: (id: number, by: string) =>
    post<{ id: number; revoked: boolean }>(`/send/authorization/${id}/revoke`, { by }),
}

export interface SendAuthorizationRecord {
  id: number
  cleared: number
  drafts: number
  authorized_by: string
  authorized_at: string
  expires_at: string
  revoked_at?: string | null
  revoked_by?: string | null
  used_at?: string | null
  released_by?: string | null
}

export interface SendAuthorizationState {
  policy: {
    campaign_id: number; slug: string; copy_approved: boolean
    agent_release_allowed: boolean; cycle_status: string
  }
  approved_drafts: number
  active: SendAuthorizationRecord | null
  history: SendAuthorizationRecord[]
  release_route_registered: boolean
}
